import React from "react";
import { Icon } from "./Icon";
import { Link } from "react-router-dom";
import { MarketingHeader } from "./MarketingHeader";
import { PageMeta } from "./PageMeta";

// Standalone marketing route — send the CTA straight to Twitch OAuth.
const handleLogin = () => {
  window.location.href = "/api/auth/twitch";
};

const PURPLE = "#9146FF";
const CYAN = "#00D4FF";
const BG = "#0a0a0a";
const TEXT = "#e2e8f0";
const MUTED = "#a0aec0";

type Cell = boolean | string;

interface CompareRow {
  feature: string;
  hal: Cell;
  them: Cell;
}

interface QA {
  q: string;
  a: string;
}

export interface ComparisonConfig {
  competitor: string; // "Nightbot"
  path: string; // "/vs/nightbot"
  title: string; // <title>
  description: string; // meta description
  h1: string;
  intro: string; // one honest paragraph under the H1
  rows: CompareRow[];
  themColumnLabel?: string; // defaults to competitor
  // Honest "when the other tool is the right pick" — builds trust + E-E-A-T.
  whenThemWinsTitle: string;
  whenThemWins: string;
  faqs: QA[];
}

const Mark: React.FC<{ cell: Cell }> = ({ cell }) => {
  if (typeof cell === "string") {
    return <span style={{ color: "#cbd5e0", fontSize: "14px" }}>{cell}</span>;
  }
  return cell ? (
    <Icon name="check" size={18} color="#10B981" />
  ) : (
    <Icon name="x" size={18} color="#64748b" />
  );
};

export const ComparisonPage: React.FC<{ config: ComparisonConfig }> = ({
  config,
}) => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 860);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const themLabel = config.themColumnLabel ?? config.competitor;

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: config.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const cellStyle: React.CSSProperties = {
    padding: isMobile ? "12px 10px" : "14px 18px",
    borderBottom: "1px solid rgba(145, 70, 255, 0.12)",
    textAlign: "center",
    verticalAlign: "middle",
  };

  return (
    <div
      style={{
        backgroundColor: BG,
        color: TEXT,
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <PageMeta
        title={config.title}
        description={config.description}
        path={config.path}
        breadcrumbs={[{ name: `vs ${config.competitor}` }]}
        jsonLd={faqLd}
      />

      <MarketingHeader onLogin={handleLogin} />

      <section
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          padding: isMobile ? "32px 20px 64px" : "48px 60px 96px",
        }}
      >
        <h1
          style={{
            fontSize: isMobile ? "36px" : "52px",
            fontWeight: 700,
            margin: "0 0 20px 0",
            letterSpacing: "-1.5px",
            lineHeight: 1.1,
            background: `linear-gradient(135deg, ${PURPLE} 0%, ${CYAN} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {config.h1}
        </h1>
        <p
          style={{
            color: MUTED,
            fontSize: "18px",
            margin: "0 0 40px 0",
            fontWeight: 300,
            lineHeight: 1.6,
            maxWidth: "720px",
          }}
        >
          {config.intro}
        </p>

        <div style={{ marginBottom: "20px" }}>
          <button onClick={handleLogin} style={ctaButton}>
            <Icon name="gamepad-2" size={18} color="white" />
            Add HAL free — 2 minutes
          </button>
        </div>

        {/* Comparison table */}
        <div style={{ overflowX: "auto", marginTop: "48px" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "520px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    ...cellStyle,
                    textAlign: "left",
                    color: MUTED,
                    fontWeight: 500,
                    fontSize: "13px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Feature
                </th>
                <th
                  style={{
                    ...cellStyle,
                    color: PURPLE,
                    fontWeight: 700,
                    fontSize: "16px",
                  }}
                >
                  HAL 8580
                </th>
                <th
                  style={{
                    ...cellStyle,
                    color: TEXT,
                    fontWeight: 600,
                    fontSize: "16px",
                  }}
                >
                  {themLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {config.rows.map((r) => (
                <tr key={r.feature}>
                  <td
                    style={{
                      ...cellStyle,
                      textAlign: "left",
                      color: "#cbd5e0",
                      fontSize: "15px",
                    }}
                  >
                    {r.feature}
                  </td>
                  <td style={cellStyle}>
                    <Mark cell={r.hal} />
                  </td>
                  <td style={cellStyle}>
                    <Mark cell={r.them} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Personality section */}
        <div style={{ marginTop: "64px", maxWidth: "760px" }}>
          <h2 style={h2Style}>
            The real difference: a personality, not a macro
          </h2>
          <p style={pStyle}>
            {config.competitor} is a command bot. You type a trigger, it prints
            the answer you pre-wrote. That&apos;s genuinely useful — and HAL
            does it too. But it&apos;s a vending machine, not a co-host.
          </p>
          <p style={pStyle}>
            HAL 8580 shows up with a <strong>character</strong> — pick the dry
            classic, the sarcastic HAL DARK, or a premium persona — and it{" "}
            <strong>remembers your regulars</strong>, reads what&apos;s
            happening on stream, and reacts in its own voice to raids, subs and
            follows. Your chat talks to it because it talks back like it&apos;s
            actually there. Nobody clips a timer message. People clip HAL.
          </p>
        </div>

        {/* Honest: when the competitor is the better pick */}
        <div style={{ marginTop: "48px", maxWidth: "760px" }}>
          <h2 style={h2Style}>{config.whenThemWinsTitle}</h2>
          <p style={pStyle}>{config.whenThemWins}</p>
        </div>

        {/* FAQ */}
        <div style={{ marginTop: "64px" }}>
          <h2 style={h2Style}>Common questions</h2>
          <div style={{ display: "grid", gap: "12px", maxWidth: "760px" }}>
            {config.faqs.map((f) => (
              <details
                key={f.q}
                style={{
                  padding: "18px 22px",
                  borderRadius: "12px",
                  background: "rgba(145, 70, 255, 0.04)",
                  border: "1px solid rgba(145, 70, 255, 0.12)",
                }}
              >
                <summary
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: TEXT,
                    cursor: "pointer",
                    listStyle: "none",
                    outline: "none",
                  }}
                >
                  {f.q}
                </summary>
                <p
                  style={{
                    color: "#cbd5e0",
                    marginTop: "14px",
                    marginBottom: 0,
                    fontSize: "15px",
                    lineHeight: 1.7,
                    fontWeight: 300,
                  }}
                >
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>

        {/* CTA + cross links */}
        <div style={{ marginTop: "64px" }}>
          <button onClick={handleLogin} style={ctaButton}>
            <Icon name="gamepad-2" size={18} color="white" />
            Give your chat a co-host — free
          </button>
          <p style={{ ...pStyle, marginTop: "28px", fontSize: "14px" }}>
            More:{" "}
            <Link style={linkStyle} to="/vs/nightbot">
              HAL vs Nightbot
            </Link>
            {"  ·  "}
            <Link style={linkStyle} to="/vs/streamelements">
              HAL vs StreamElements
            </Link>
            {"  ·  "}
            <Link style={linkStyle} to="/vs/ai-licia">
              HAL vs ai-licia
            </Link>
            {"  ·  "}
            <Link style={linkStyle} to="/best-twitch-ai-chatbot">
              Best Twitch AI chatbots
            </Link>
            {"  ·  "}
            <Link style={linkStyle} to="/faq">
              FAQ
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

const ctaButton: React.CSSProperties = {
  background: `linear-gradient(135deg, ${PURPLE} 0%, ${CYAN} 100%)`,
  color: "white",
  border: "none",
  padding: "14px 28px",
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "12px",
  cursor: "pointer",
  boxShadow: "0 8px 25px rgba(145, 70, 255, 0.4)",
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
};

const h2Style: React.CSSProperties = {
  fontSize: "26px",
  fontWeight: 700,
  color: TEXT,
  margin: "0 0 16px 0",
  letterSpacing: "-0.5px",
};

const pStyle: React.CSSProperties = {
  color: "#cbd5e0",
  fontSize: "16px",
  lineHeight: 1.7,
  fontWeight: 300,
  margin: "0 0 16px 0",
};

const linkStyle: React.CSSProperties = {
  color: CYAN,
  textDecoration: "none",
};

// ---- Nightbot -------------------------------------------------------------

const NIGHTBOT: ComparisonConfig = {
  competitor: "Nightbot",
  path: "/vs/nightbot",
  title: "HAL 8580 vs Nightbot — a Nightbot Alternative With Personality",
  description:
    "Nightbot runs commands and timers. HAL 8580 is an AI co-host with a personality that remembers your regulars and reacts to your stream. Compare them — both free.",
  h1: "HAL 8580 vs Nightbot",
  intro:
    "Nightbot is the reliable command bot everyone starts with. HAL 8580 is the AI co-host you graduate to — a Nightbot alternative with an actual personality, memory of your regulars, and reactions to what's happening on stream. Both have a free tier. Here's the honest breakdown.",
  rows: [
    {
      feature: "AI personalities (dry, sarcastic, more)",
      hal: true,
      them: false,
    },
    { feature: "Remembers your regular viewers", hal: true, them: false },
    {
      feature: "Conversational, context-aware replies",
      hal: true,
      them: false,
    },
    {
      feature: "Reacts to raids / subs / follows in character",
      hal: true,
      them: "Timers only",
    },
    { feature: "Custom commands & keyword triggers", hal: true, them: true },
    { feature: "Chat analytics & sentiment", hal: "Premium", them: false },
    { feature: "Spam filters & moderation", hal: "Basic", them: true },
    { feature: "Song requests", hal: false, them: true },
    { feature: "Free tier", hal: true, them: true },
    { feature: "Price", hal: "Free · Premium $4.99/mo", them: "Free" },
  ],
  whenThemWinsTitle: "When Nightbot is the better pick",
  whenThemWins:
    "If all you want is bulletproof moderation, timers, and song requests with zero fuss, Nightbot is excellent and completely free — keep it. Plenty of streamers run both: Nightbot for the housekeeping, HAL for the personality. They don't conflict.",
  faqs: [
    {
      q: "Is HAL 8580 a free Nightbot alternative?",
      a: "Yes — HAL has a free tier that joins your Twitch chat, takes on a personality, and reacts to your stream. Premium ($4.99/mo) unlocks extra personalities and 7- and 30-day analytics. You can run HAL alongside Nightbot; they don't step on each other.",
    },
    {
      q: "Can HAL do custom commands like Nightbot?",
      a: "Yes. HAL supports custom commands and keyword triggers. The difference is that HAL can also respond conversationally and in character, not only with fixed pre-written text.",
    },
    {
      q: "Does HAL replace Nightbot's moderation?",
      a: "Not entirely. Nightbot has stronger dedicated spam and moderation tooling. Many streamers keep Nightbot for moderation and add HAL as the AI co-host in chat.",
    },
    {
      q: "How long does it take to switch or add HAL?",
      a: "About two minutes. Log in with Twitch, pick a personality, mod the bot, and HAL is live in your chat.",
    },
  ],
};

// ---- StreamElements -------------------------------------------------------

const STREAMELEMENTS: ComparisonConfig = {
  competitor: "StreamElements",
  path: "/vs/streamelements",
  title: "StreamElements Alternative for Chat — HAL 8580 vs StreamElements",
  description:
    "Looking for a StreamElements alternative for your chat? StreamElements does overlays and alerts; HAL 8580 is the AI co-host with a personality that lives in your chat. Compare them — and why many streamers run both.",
  h1: "HAL 8580 vs StreamElements",
  intro:
    "If you're after a StreamElements alternative, know what you're replacing. StreamElements is a whole production suite — overlays, alerts, loyalty points, tipping. HAL 8580 does the one thing it doesn't: it's an AI co-host with a personality that lives in your chat, remembers regulars, and reacts on its own. For most streamers this isn't either/or.",
  rows: [
    { feature: "AI personality in chat", hal: true, them: false },
    { feature: "Remembers your regular viewers", hal: true, them: false },
    {
      feature: "Conversational, context-aware replies",
      hal: true,
      them: false,
    },
    {
      feature: "Reacts to events in character",
      hal: true,
      them: "Alerts only",
    },
    { feature: "Custom commands & triggers", hal: true, them: true },
    { feature: "Overlays & on-screen alerts", hal: false, them: true },
    { feature: "Loyalty points & tipping", hal: false, them: true },
    { feature: "Chat analytics & sentiment", hal: "Premium", them: "Basic" },
    { feature: "Free tier", hal: true, them: true },
    { feature: "Price", hal: "Free · Premium $4.99/mo", them: "Free" },
  ],
  whenThemWinsTitle: "When StreamElements is the better pick",
  whenThemWins:
    "If you need overlays, on-screen alerts, a loyalty-points economy, or tipping, that's StreamElements' home turf — HAL doesn't do any of that. The smart move for most streamers is to run StreamElements for the on-screen production and add HAL for the AI personality in chat.",
  faqs: [
    {
      q: "Does HAL 8580 replace StreamElements?",
      a: "No — and it isn't meant to. StreamElements handles overlays, alerts, loyalty and tipping. HAL is the AI co-host in your chat. They complement each other; many streamers run both.",
    },
    {
      q: "Can I use HAL and StreamElements at the same time?",
      a: "Yes. They operate in different lanes — StreamElements on-screen and for economy features, HAL for conversational chat personality — so they don't conflict.",
    },
    {
      q: "Is HAL free like StreamElements?",
      a: "HAL has a free tier. Premium ($4.99/mo) adds extra personalities and deeper analytics. StreamElements is free too, so you can layer HAL on at no cost.",
    },
  ],
};

// ---- ai-licia -------------------------------------------------------------

const AILICIA: ComparisonConfig = {
  competitor: "ai-licia",
  themColumnLabel: "ai-licia",
  path: "/vs/ai-licia",
  title: "ai-licia Alternative — HAL 8580 vs ai-licia for Twitch",
  description:
    "ai-licia is a feature-rich, multi-platform AI co-host with voice. HAL 8580 is the focused, Twitch-native, genuinely-free alternative with a personality of its own. Honest comparison.",
  h1: "HAL 8580 vs ai-licia",
  intro:
    "ai-licia is the established AI co-host — voice, multi-platform, 10k+ streamers. HAL 8580 is the scrappier, Twitch-native alternative: no voice, no five-platform setup, just a distinct personality that lives in your chat, remembers your regulars, and reacts to your stream — with a genuinely free tier. Here's the honest breakdown.",
  rows: [
    { feature: "AI personality in chat", hal: true, them: true },
    {
      feature: "Ready-made personalities (incl. sarcastic HAL DARK)",
      hal: true,
      them: "Build your own",
    },
    { feature: "Remembers your regular viewers", hal: true, them: true },
    {
      feature: "Reacts to raids / subs / follows in character",
      hal: true,
      them: true,
    },
    { feature: "Voice — speaks out loud & listens", hal: false, them: true },
    {
      feature: "Multi-platform (TikTok, Discord, Kick, YouTube)",
      hal: false,
      them: true,
    },
    { feature: "Twitch-native, minimal setup", hal: true, them: "Does more" },
    { feature: "Free tier", hal: true, them: "Trial" },
    { feature: "Price", hal: "Free · Premium $4.99/mo", them: "Paid plans" },
  ],
  whenThemWinsTitle: "When ai-licia is the better pick",
  whenThemWins:
    "ai-licia is the more full-featured product. If you want an AI co-host that speaks out loud with its own voice, or one bot running across Twitch, TikTok, Discord, Kick and YouTube at once, ai-licia does things HAL simply doesn't. HAL's bet is the opposite: focused on Twitch chat, fast to set up, a personality with actual character, and free to start.",
  faqs: [
    {
      q: "Is HAL 8580 a free ai-licia alternative?",
      a: "HAL has a free tier and is Twitch-focused. It's a good fit if you want a simple, free AI co-host that lives in your Twitch chat with a personality and viewer memory. ai-licia is the better choice if you need voice or multi-platform coverage.",
    },
    {
      q: "Does HAL have a voice like ai-licia?",
      a: "No. HAL is text chat only — it replies in your Twitch chat, it doesn't speak out loud. If a spoken AI voice matters to you, ai-licia does that and HAL doesn't. HAL focuses on in-chat personality, memory and event reactions.",
    },
    {
      q: "Which is cheaper, HAL or ai-licia?",
      a: "HAL has a genuinely free tier and a $4.99/mo Premium, and it only targets Twitch — so it's the budget-friendly, focused option. ai-licia is a broader, paid product.",
    },
    {
      q: "Does HAL work on multiple platforms like ai-licia?",
      a: "Today HAL is Twitch only. Other platforms (YouTube Live, Kick) are on the roadmap but not committed to a date. ai-licia is multi-platform right now.",
    },
  ],
};

export const VsNightbot: React.FC = () => <ComparisonPage config={NIGHTBOT} />;
export const VsAilicia: React.FC = () => <ComparisonPage config={AILICIA} />;
export const VsStreamElements: React.FC = () => (
  <ComparisonPage config={STREAMELEMENTS} />
);
